import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { STATUS, fmtClock } from "../lib/labels";

// Mapa desenhado diretamente em coordenadas do mundo GTA V (ver /app/scripts/gen_map.py)
export const MAP = { xMin: -4500, xMax: 5000, yMin: -4500, yMax: 8500 };
const BOUNDS = [[MAP.yMin, MAP.xMin], [MAP.yMax, MAP.xMax]];
const PAD = 2500;
const NEUTRAL = "#64748b";

// idle: cor do dono | active: pisca (guerra: cor de quem declarou; conquista: cor de quem conquista)
// contested: alterna conquistador/dono | faded: conquista em espera (translúcida)
export const zoneVisual = (z) => {
  const w = z.active_war;
  const c = z.capture;
  const owner = z.owner?.color || NEUTRAL;
  if (w) return { state: "active", a: w.attacker?.color || NEUTRAL, d: w.defender?.color || NEUTRAL };
  if (c) {
    const a = c.org?.color || NEUTRAL;
    return { state: c.status === "contested" ? "contested" : c.status === "standby" ? "faded" : "active", a, d: owner };
  }
  return { state: "idle", a: owner, d: owner };
};

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

const remaining = (w, receivedAt) =>
  Math.max(0, (w.seconds_remaining || 0) - Math.floor((Date.now() - receivedAt) / 1000));

const CAP_STATUS = { capturing: "A conquistar", contested: "Contestada", standby: "Em espera" };

function tooltipHtml(z, receivedAt) {
  const owner = z.owner ? `<b style="color:${esc(z.owner.color)}">${esc(z.owner.label)}</b>` : `<span class="zt-muted">Sem dono</span>`;
  let war = `<div class="zt-muted">Sem guerra ativa</div>`;
  const w = z.active_war;
  if (w) {
    const secs = remaining(w, receivedAt);
    const label = w.status === "fight" && (w.overtime || secs === 0) ? "Prolongamento" : fmtClock(secs);
    war = `<div class="zt-war zt-${w.status}">${STATUS[w.status]?.label || w.status} · <span class="zt-clock">${label}</span></div>`
      + `<div class="zt-vs">${esc(w.attacker?.label)} <i>vs</i> ${esc(w.defender?.label || "—")}</div>`;
  }
  const c = z.capture;
  const cap = c ? `<div>Conquista: <b style="color:${esc(c.org?.color)}">${esc(c.org?.label)}</b> ${Number(c.progress).toFixed(1)}% · ${CAP_STATUS[c.status] || c.status}</div>` : "";
  return `<div class="zt"><div class="zt-title">${esc(z.name)}</div><div>Dono: ${owner}</div>${cap}${war}</div>`;
}

export const ZoneMap = ({ zones, height = 540, testid = "zone-map", interactive = true, focus = null }) => {
  const elRef = useRef(null);
  const layerRef = useRef(null);
  const itemsRef = useRef([]);
  const recvRef = useRef(Date.now());

  useEffect(() => {
    const lock = interactive ? {} : {
      dragging: false, scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false, keyboard: false, touchZoom: false, zoomControl: false,
    };
    const map = L.map(elRef.current, {
      crs: L.CRS.Simple, minZoom: -5, maxZoom: 1, zoomSnap: 0.25, zoomDelta: 0.5, attributionControl: false,
      maxBounds: [[MAP.yMin - PAD, MAP.xMin - PAD], [MAP.yMax + PAD, MAP.xMax + PAD]], ...lock,
    });
    L.imageOverlay(`${process.env.PUBLIC_URL || ""}/map/gta-map.svg`, BOUNDS).addTo(map);
    const view = focus || BOUNDS;
    map.fitBounds(view);
    // dentro de diálogos animados o tamanho só fica correto depois da animação
    const refit = setTimeout(() => { map.invalidateSize(); if (focus) map.fitBounds(view); }, 300);
    layerRef.current = L.layerGroup().addTo(map);
    const t = setInterval(() => {
      itemsRef.current.forEach(({ marker, zone }) => (zone.active_war || zone.capture) && marker.getTooltip() && marker.setTooltipContent(tooltipHtml(zone, recvRef.current)));
    }, 1000);
    return () => {
      clearTimeout(refit);
      clearInterval(t);
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer || !zones) return;
    recvRef.current = Date.now();
    layer.clearLayers();
    itemsRef.current = [];
    zones.forEach((z) => {
      const v = zoneVisual(z);
      const faded = v.state === "faded";
      const style = { color: v.a, fillColor: v.a, opacity: faded ? 0.45 : 1, dashArray: faded ? "6 6" : null, className: `zst zst-${v.state}`, interactive };
      const ll = [z.coords.y, z.coords.x];
      const circle = L.circle(ll, { ...style, radius: z.radius, weight: 2, fillOpacity: faded ? 0.12 : 0.35 }).addTo(layer);
      const marker = L.circleMarker(ll, { ...style, radius: 9, weight: 3, fillOpacity: faded ? 0.3 : 0.9 }).addTo(layer);
      [circle, marker].forEach((l) => {
        const el = l.getElement();
        el?.style.setProperty("--za", v.a);
        el?.style.setProperty("--zd", v.d);
      });
      if (interactive) marker.bindTooltip(tooltipHtml(z, recvRef.current), { direction: "top", offset: [0, -10], className: "zone-tooltip" });
      marker.getElement()?.setAttribute("data-testid", `map-zone-${z.id}`);
      marker.getElement()?.setAttribute("data-state", v.state);
      itemsRef.current.push({ marker, zone: z });
    });
  }, [zones, interactive]);

  return <div ref={elRef} data-testid={testid} style={{ height }} className="zone-map w-full border border-slate-800" />;
};

export const MapLegend = ({ orgs }) => (
  <div className="flex flex-wrap items-center gap-4 font-mono text-[11px] uppercase tracking-widest text-slate-400" data-testid="map-legend">
    {(orgs || []).map((o) => (
      <span key={o.id} className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: o.color }} />{o.label}</span>
    ))}
    <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full bg-slate-500" />Sem dono</span>
    <span className="flex items-center gap-2" data-testid="legend-active"><span className="lg-blink h-3 w-3 rounded-full bg-slate-200" />A conquistar / em guerra (pisca)</span>
    <span className="flex items-center gap-2" data-testid="legend-contested"><span className="lg-alt h-3 w-3 rounded-full" />Conquista contestada (alterna cores)</span>
    <span className="flex items-center gap-2" data-testid="legend-faded"><span className="h-3 w-3 rounded-full border border-dashed border-slate-200 bg-slate-200/30" />Conquista em espera</span>
  </div>
);
